import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiHome, FiFileText, FiDollarSign } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const benefits = [
  {
    title: "Compare 70+ lenders",
    description:
      "Access products from major banks, credit unions, and specialist lenders - all in one application.",
  },
  {
    title: "Free service",
    description:
      "We're paid by the lender when your loan settles. Our service costs you nothing.",
  },
  {
    title: "Expert guidance",
    description:
      "We want you as clients for the long-term and will assist you even after settlement. From pre-approval to settlement and beyond, we're here for you.",
  },
  {
    title: "Best interest duty",
    description:
      "We're legally required to act in your best interest - not just sell you any product.",
  },
];

const steps = [
  {
    step: 1,
    title: "Get pre-approved",
    description:
      "Know your budget before you start looking. Pre-approval gives you confidence and shows sellers you're serious.",
    link: { label: "Check borrowing power", href: "/calculators/borrowing-power" },
  },
  {
    step: 2,
    title: "Find your property",
    description:
      "Research areas, attend inspections, and make informed decisions. We're here to answer questions along the way.",
    link: { label: "Read our guide", href: "/guides/loan-process" },
  },
  {
    step: 3,
    title: "Make an offer",
    description:
      "Once you find the right property, make an offer subject to finance. We'll fast-track your formal approval.",
    link: null,
  },
  {
    step: 4,
    title: "Settlement",
    description:
      "We coordinate with your conveyancer and lender to ensure everything runs smoothly on settlement day.",
    link: null,
  },
];

const faqs = [
  {
    question: "How much deposit do I need?",
    answer:
      "Traditionally, 20% is ideal to avoid Lenders Mortgage Insurance (LMI). However, some lenders accept as little as 5% deposit, and government schemes can help first home buyers enter with just 2-5% deposit.",
  },
  {
    question: "What documents do I need for a loan application?",
    answer:
      "Typically you'll need: proof of identity (passport/licence), proof of income (payslips, tax returns), bank statements, and details of any existing debts. We'll provide a complete checklist based on your situation.",
  },
  {
    question: "How long does pre-approval take?",
    answer:
      "Pre-approval can often be obtained within 24-48 hours for straightforward applications. More complex situations may take a few days. Pre-approval is typically valid for 3-6 months.",
  },
  {
    question: "Should I use a fixed or variable rate?",
    answer:
      "It depends on your circumstances and risk appetite. Fixed rates offer certainty, while variable rates are often lower and more flexible. Many borrowers split their loan to get the benefits of both.",
  },
  {
    question: "What costs are involved in buying a home?",
    answer:
      "Beyond the deposit, budget for stamp duty (varies by state), legal/conveyancing fees ($1,500-$3,000), building/pest inspections ($300-$800), and moving costs. Use our stamp duty calculator for an estimate.",
  },
];

export function BuyingAHomePage() {
  return (
    <>
      <SEO
        title="Buying a Home Springfield, Brisbane & Logan"
        description="Buying a home in Springfield, Brisbane, Logan or Ipswich? Compare 70+ lenders, get pre-approval fast, expert broker support to settlement."
        url="/home-loans/buying-a-home"
        keywords={[
          "buying a home Springfield",
          "buying a home Brisbane",
          "home loan pre-approval Brisbane",
          "next home buyer",
          "upgrader home loan",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Home Buyer Loans",
            description:
              "Home loan broking for owner-occupier home buyers — pre-approval, lender comparison, full settlement support.",
            url: "/home-loans/buying-a-home",
            serviceType: "Home Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
            { name: "Buying a Home", url: "/home-loans/buying-a-home" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/home-loans"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <FiArrowRight className="size-4 mr-2 rotate-180" />
              Home Loans
            </Link>
            <p className="section-label mb-4">Home Purchase</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Buying a Home
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Whether it's your forever home or a stepping stone, buying property
              is one of life's biggest decisions. We make the finance part simple,
              so you can focus on finding the right place.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Get Pre-Approved
                <FiArrowRight className="size-4" />
              </Link>
              <Link
                to="/calculators/borrowing-power"
                className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
              >
                Check Borrowing Power
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="p-6 bg-muted/30 rounded-2xl">
                <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center mb-4">
                  <FiCheck className="size-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">The Journey</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              From search to settlement
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We guide you through every step of the home buying process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {steps.map((item) => (
              <div
                key={item.step}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-xl flex items-center justify-center font-serif text-xl flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-muted-foreground text-sm mb-3">
                      {item.description}
                    </p>
                    {item.link && (
                      <Link
                        to={item.link.href}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                      >
                        {item.link.label}
                        <FiArrowRight className="size-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you need */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="section-label mb-4">Preparation</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                What you'll need
              </h2>
              <p className="text-muted-foreground mb-8">
                Being prepared makes the process faster and smoother. Here's what
                lenders typically require for a home loan application.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl">
                  <FiFileText className="size-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium mb-1">Identity documents</h4>
                    <p className="text-sm text-muted-foreground">
                      Passport, driver's licence, or other government-issued ID
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl">
                  <FiDollarSign className="size-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium mb-1">Income evidence</h4>
                    <p className="text-sm text-muted-foreground">
                      Recent payslips, tax returns, or business financials if
                      self-employed
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl">
                  <FiHome className="size-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium mb-1">Asset & liability details</h4>
                    <p className="text-sm text-muted-foreground">
                      Bank statements, existing loans, credit cards, and savings
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-primary text-primary-foreground rounded-3xl p-8">
              <p className="section-label mb-4">
                Quick Tip
              </p>
              <h3 className="font-serif text-2xl mb-4">
                Get pre-approved before you start looking
              </h3>
              <p className="text-primary-foreground/80 mb-6">
                Pre-approval gives you a clear budget, shows sellers you're serious,
                and lets you act quickly when you find the right property. It's free
                and usually takes just 24-48 hours.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-2.5 rounded-full font-medium hover:bg-accent/90 transition-colors"
              >
                Get pre-approved
                <FiArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">FAQs</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              Common questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold mb-2">{faq.question}</h3>
                <p className="text-muted-foreground text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to buy?"
        title="Let's find the right loan for your home purchase"
        description="Book a free consultation and we'll guide you through the process."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
